/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AuthenticationHandler, Client, HTTPMessageHandler, RetryHandler, RetryHandlerOptions, TelemetryHandler } from '@microsoft/microsoft-graph-client';
import { SessionCache, storageAvailable } from '../utils/SessionCache';
import { Graph } from '../Graph';
import { chainMiddleware } from '../utils/GraphHelpers';
/**
 * MockGraph Instance
 *
 * @export
 * @class MockGraph
 * @extends {Graph}
 */
// tslint:disable-next-line: max-classes-per-file
export class MockGraph extends Graph {
    constructor(mockProvider) {
        const middleware = [
            new AuthenticationHandler(mockProvider),
            new RetryHandler(new RetryHandlerOptions()),
            new TelemetryHandler(),
            new MockMiddleware(),
            new HTTPMessageHandler()
        ];
        super(Client.initWithMiddleware({
            middleware: chainMiddleware(...middleware)
        }));
    }
    /**
     * Returns an instance of the Graph in the context of the provided component.
     *
     * @param {MgtBaseComponent} component
     * @returns
     * @memberof Graph
     */
    forComponent(component) {
        // The purpose of the forComponent pattern is to update the headers of any outgoing Graph requests.
        // The MockGraph isn't making real Graph requests, so we can simply no-op and return the same instance.
        return this;
    }
}
/**
 * Implements Middleware for the Mock Client to escape
 * the graph url from the request
 *
 * @class MockMiddleware
 * @implements {Middleware}
 */
// tslint:disable-next-line: max-classes-per-file
class MockMiddleware {
    constructor() {
        if (storageAvailable('sessionStorage')) {
            this._session = new SessionCache();
        }
    }
    // tslint:disable-next-line: completed-docs
    execute(context) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const baseUrl = yield this.getBaseUrl();
                context.request = baseUrl + encodeURIComponent(context.request);
            }
            catch (error) {
                // ignore error
            }
            return yield this._nextMiddleware.execute(context);
        });
    }
    /**
     * Handles setting of next middleware
     *
     * @param {Middleware} next
     * @memberof SdkVersionMiddleware
     */
    setNext(next) {
        this._nextMiddleware = next;
    }
    getBaseUrl() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._baseUrl) {
                const sessionEndpoint = (_a = this._session) === null || _a === void 0 ? void 0 : _a.getItem('endpointURL');
                if (sessionEndpoint) {
                    this._baseUrl = sessionEndpoint;
                }
                else {
                    try {
                        // get the url we should be using from the endpoint service
                        let response = yield fetch('https://cdn.graph.office.net/en-us/graph/api/proxy/endpoint');
                        this._baseUrl = (yield response.json()) + '?url=';
                    }
                    catch (_c) {
                        // fallback to hardcoded value
                        this._baseUrl = 'https://proxy.apisandbox.msdn.microsoft.com/svc?url=';
                    }
                    (_b = this._session) === null || _b === void 0 ? void 0 : _b.setItem('endpointURL', this._baseUrl);
                }
            }
            return this._baseUrl;
        });
    }
}
//# sourceMappingURL=MockGraph.js.map